/**
 * `<McpTokenPanel>` — the mint / configure / advanced sections of the MCP
 * Integration card (U4 split out of McpIntegration.tsx; behavior unchanged).
 *
 * Owns the whole token lifecycle on THIS device:
 *   - Step 1 (universal): mint a device token. Desktop hands the plaintext to
 *     the OS keychain and forgets it; web renders it ONCE with a copy button.
 *   - Step 2: desktop one-click Claude Desktop config write; web manual-paste
 *     snippet with the token substituted in.
 *   - Advanced `<details>`: raw snippet + (desktop) local HTTP endpoint with
 *     the 30 s reveal flow.
 *
 * `children` render between Step 2 and Advanced — the devices-table slot —
 * so the section order matches the pre-split card exactly.
 *
 * Desktop/web branches on `isDesktopApp()` inline where the UI genuinely
 * differs; the keychain/config-writer IPC lives in ./desktopMcp.ts.
 */

import { useCallback, useEffect, useState } from 'react';
import type { ReactNode } from 'react';
import { Input } from '@/components/ui';
import { callEndpoint } from '@/api';
import { isDesktopApp } from '@/desktop/isDesktopApp';
import {
  engageDirectBackend,
  forgetDeviceToken,
  loadDesktopMcpSnapshot,
  revealStoredToken,
  storeTokenInKeychain,
  writeClaudeDesktopConfig,
  type ConfigureClaudeResult,
} from './desktopMcp';

// Claude Desktop's config-file path differs by OS. The Rust IPC writes to the
// correct path (BaseDirs::config_dir); this is just the user-visible hint shown
// in the panel. UA-sniff is fine — Tauri's WebView2 on Windows reports
// "Windows", WKWebView on macOS reports "Mac OS", and on the web build the UA
// matches the user's actual machine OS.
const IS_MAC =
  typeof navigator !== 'undefined' && /Mac OS/.test(navigator.userAgent);
const CLAUDE_CONFIG_PATH = IS_MAC
  ? '~/Library/Application Support/Claude/claude_desktop_config.json'
  : '%APPDATA%\\Claude\\claude_desktop_config.json';

interface MintResponse {
  id: string;
  token: string;
  label: string;
  created_at: string;
}

type AsyncState = 'idle' | 'pending' | 'success' | 'error';

function defaultLabel(desktop: boolean): string {
  if (desktop) return 'Aletheia desktop';
  // Browser UA sniff just for a friendly default — user can rename anyway.
  const ua = typeof navigator !== 'undefined' ? navigator.userAgent : '';
  if (/Windows/.test(ua)) return 'Web (Windows)';
  if (/Mac OS/.test(ua)) return 'Web (Mac)';
  if (/Linux/.test(ua)) return 'Web (Linux)';
  return 'Web';
}

interface McpTokenPanelProps {
  /** Clerk user id — the workspace key for keychain storage (v0.1.14). */
  workspaceId: string;
  /** Fired after a successful mint so the devices table can refresh. */
  onMinted: () => void;
  /** Rendered between Step 2 and the Advanced details (devices-table slot). */
  children?: ReactNode;
}

export function McpTokenPanel({
  workspaceId,
  onMinted,
  children,
}: McpTokenPanelProps): JSX.Element {
  const desktop = isDesktopApp();

  // --- Desktop-only keychain status ---------------------------------------
  const [keychainHasToken, setKeychainHasToken] = useState<boolean>(false);
  const [keychainMasked, setKeychainMasked] = useState<string | null>(null);
  const [installPath, setInstallPath] = useState<string>('');
  const [localUrl, setLocalUrl] = useState<string>('');
  const [revealed, setRevealed] = useState<string | null>(null);

  // --- Universal mint state -----------------------------------------------
  const [label, setLabel] = useState<string>(defaultLabel(desktop));
  const [mintState, setMintState] = useState<AsyncState>('idle');
  const [mintError, setMintError] = useState<string | null>(null);
  /** Plaintext we just minted. Desktop: cleared as soon as it's in the keychain.
   *  Web: kept on-screen with a copy button + warning — there is no other place
   *  for it to live. The user is told this is their one chance. */
  const [freshlyMintedToken, setFreshlyMintedToken] = useState<string | null>(null);

  // --- "Configure Claude Desktop" (desktop only) --------------------------
  const [configResult, setConfigResult] = useState<ConfigureClaudeResult | null>(null);
  const [configState, setConfigState] = useState<AsyncState>('idle');

  // Hydrate IPC state (desktop only) on mount. Token status is per-workspace
  // (v0.1.14), so wait for the Clerk user id before fetching it.
  useEffect(() => {
    if (!desktop || !workspaceId) return;
    let cancelled = false;
    void (async () => {
      try {
        const snap = await loadDesktopMcpSnapshot(workspaceId);
        if (cancelled) return;
        setKeychainHasToken(snap.hasToken);
        setKeychainMasked(snap.masked);
        setInstallPath(snap.installPath);
        setLocalUrl(snap.localUrl);
        // If a token is already stored for this workspace from a prior mint,
        // engage the direct-to-Azure chat routing now (v0.1.14: replaces the
        // unconditional boot-time call in main.tsx, which couldn't pass a
        // workspace id before Clerk had resolved the user).
        if (snap.hasToken) {
          void engageDirectBackend(workspaceId);
        }
      } catch (err) {
        // eslint-disable-next-line no-console
        console.warn('[mcp] desktop IPC unavailable', err);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [desktop, workspaceId]);

  // Auto-hide the revealed token (desktop reveal flow) after 30 s.
  useEffect(() => {
    if (revealed === null) return;
    const t = window.setTimeout(() => setRevealed(null), 30_000);
    return () => window.clearTimeout(t);
  }, [revealed]);

  const onMintToken = useCallback(async () => {
    if (desktop && !workspaceId) {
      setMintError('Sign in first to mint a device token.');
      setMintState('error');
      return;
    }
    setMintState('pending');
    setMintError(null);
    setFreshlyMintedToken(null);
    try {
      const resp = await callEndpoint<MintResponse>('POST', 'system/mcp_token/mint', {
        body: { label: label.trim() || defaultLabel(desktop) },
      });
      if (!resp.token) throw new Error('mint endpoint returned no token');

      if (desktop) {
        // Hand off to the OS credential store immediately and forget the plaintext.
        const status = await storeTokenInKeychain(workspaceId, resp.token);
        setKeychainHasToken(status.hasToken);
        setKeychainMasked(status.masked);
        // Now that a token exists in the store, switch the desktop over to
        // direct-to-Azure routing (chat SSE streaming + no proxy double-fire).
        await engageDirectBackend(workspaceId);
      } else {
        // Web has nowhere safe to put it — show it once with a warning.
        setFreshlyMintedToken(resp.token);
      }
      setMintState('success');
      onMinted();
    } catch (err) {
      setMintError(err instanceof Error ? err.message : String(err));
      setMintState('error');
    }
  }, [desktop, label, onMinted, workspaceId]);

  const onConfigureClaude = useCallback(async () => {
    if (!workspaceId) return;
    setConfigState('pending');
    setConfigResult(null);
    try {
      const result = await writeClaudeDesktopConfig(workspaceId);
      setConfigResult(result);
      setConfigState(result.ok ? 'success' : 'error');
    } catch (err) {
      setConfigResult({
        ok: false,
        path: null,
        message: err instanceof Error ? err.message : String(err),
      });
      setConfigState('error');
    }
  }, [workspaceId]);

  const onReveal = useCallback(async () => {
    if (!workspaceId) return;
    try {
      const t = await revealStoredToken(workspaceId);
      setRevealed(t);
    } catch (err) {
      // eslint-disable-next-line no-console
      console.warn('[mcp] reveal failed', err);
    }
  }, [workspaceId]);

  const onCopy = useCallback((text: string) => {
    void navigator.clipboard.writeText(text);
  }, []);

  const onRevokeLocal = useCallback(async () => {
    if (!desktop || !workspaceId) return;
    const status = await forgetDeviceToken(workspaceId);
    setKeychainHasToken(status.hasToken);
    setKeychainMasked(status.masked);
    setRevealed(null);
  }, [desktop, workspaceId]);

  // Snippet shown in "Advanced". On desktop we know the install path; on
  // web we substitute the freshly-minted token (if visible) so the user can
  // copy a fully-baked snippet.
  const claudeConfigSnippet = JSON.stringify(
    {
      mcpServers: {
        aletheia: desktop
          ? {
              command: installPath || '<aletheia.exe path>',
              args: ['--mcp-stdio'],
            }
          : {
              command: '<aletheia.exe path>',
              args: ['--mcp-stdio'],
              env: {
                ALETHEIA_MCP_TOKEN: freshlyMintedToken ?? '<your token>',
              },
            },
      },
    },
    null,
    2,
  );

  // --- Render -------------------------------------------------------------

  return (
    <>
      {/* ---------------- Step 1: Mint ---------------- */}
      <div className="mcp-section">
        <h4>Step 1: Mint a device token</h4>
        <p className="mcp-hint">
          {desktop
            ? 'Stored locally on this device. The plaintext never appears in a config file or your settings.'
            : 'You will see the token exactly once. Copy it now — we cannot show it again later. Use Revoke if it leaks.'}
        </p>

        {desktop && keychainHasToken ? (
          <div className="mcp-row mcp-row--success">
            <span>✓ Token stored ({keychainMasked ?? '••••'})</span>
            <button
              type="button"
              className="btn btn--sm"
              onClick={() => void onMintToken()}
              disabled={mintState === 'pending'}
            >
              {mintState === 'pending' ? 'Re-minting…' : 'Rotate token'}
            </button>
            <button type="button" className="btn btn--sm" onClick={() => void onRevokeLocal()}>
              Forget this device
            </button>
          </div>
        ) : (
          <>
            <div className="mcp-row">
              <label htmlFor="mcp-label" className="mcp-hint">
                Label:
              </label>
              <Input
                id="mcp-label"
                type="text"
                value={label}
                onChange={(e) => setLabel(e.target.value)}
                placeholder={defaultLabel(desktop)}
                className="settings-input--compact"
              />
              <button
                type="button"
                className="btn btn--primary"
                onClick={() => void onMintToken()}
                disabled={mintState === 'pending'}
              >
                {mintState === 'pending' ? 'Minting…' : 'Mint device token'}
              </button>
              {mintError !== null && <span className="mcp-error">{mintError}</span>}
            </div>

            {!desktop && freshlyMintedToken !== null && (
              <div className="mcp-row mcp-row--success settings-gap-top">
                <strong className="settings-callout">
                  ⚠ Copy this token now — it will not be shown again.
                </strong>
                <pre className="mcp-snippet">{freshlyMintedToken}</pre>
                <div className="mcp-row">
                  <button
                    type="button"
                    className="btn btn--sm"
                    onClick={() => onCopy(freshlyMintedToken)}
                  >
                    Copy token
                  </button>
                  <button
                    type="button"
                    className="btn btn--sm"
                    onClick={() => setFreshlyMintedToken(null)}
                  >
                    I've copied it — dismiss
                  </button>
                </div>
              </div>
            )}
          </>
        )}
      </div>

      {/* ---------------- Step 2: Configure client ---------------- */}
      {desktop ? (
        <div className="mcp-section">
          <h4>Step 2: Configure Claude Desktop</h4>
          <p className="mcp-hint">
            One click writes the right entry into{' '}
            <code>{CLAUDE_CONFIG_PATH}</code>. Other MCP servers you have
            configured stay untouched.
          </p>
          <div className="mcp-row">
            <button
              type="button"
              className="btn btn--primary"
              onClick={() => void onConfigureClaude()}
              disabled={!keychainHasToken || configState === 'pending'}
              title={
                keychainHasToken
                  ? 'Patch Claude Desktop config'
                  : 'Mint a token first (step 1)'
              }
            >
              {configState === 'pending' ? 'Writing…' : 'Configure Claude Desktop'}
            </button>
            {configResult !== null && (
              <span className={configResult.ok ? 'mcp-success' : 'mcp-error'}>
                {configResult.ok ? '✓ ' : '✕ '}
                {configResult.message}
              </span>
            )}
          </div>
          {configResult !== null && configResult.ok && (
            <p className="mcp-hint">Restart Claude Desktop to load the new server.</p>
          )}
        </div>
      ) : (
        <div className="mcp-section">
          <h4>Step 2: Paste into your MCP client config</h4>
          <p className="mcp-hint">
            The Aletheia desktop app does this in one click. From the browser, paste the snippet
            below into <code>{CLAUDE_CONFIG_PATH}</code> (or your client's
            equivalent). Restart the client to pick up the change.
          </p>
          <pre className="mcp-snippet">{claudeConfigSnippet}</pre>
          <div className="mcp-row">
            <button
              type="button"
              className="btn btn--sm"
              onClick={() => onCopy(claudeConfigSnippet)}
            >
              Copy snippet
            </button>
            <a
              className="btn btn--sm"
              href="https://github.com/anthropics/claude-desktop"
              target="_blank"
              rel="noreferrer"
            >
              Download Aletheia Desktop ↗
            </a>
          </div>
        </div>
      )}

      {/* Devices-table slot — keeps the pre-split section order. */}
      {children}

      {/* ---------------- Advanced ---------------- */}
      <details className="mcp-section">
        <summary>Advanced — snippet + non-Claude clients</summary>
        <p className="mcp-hint">
          If the auto-configure didn't work, paste this into{' '}
          <code>{CLAUDE_CONFIG_PATH}</code> manually:
        </p>
        <pre className="mcp-snippet">{claudeConfigSnippet}</pre>
        <div className="mcp-row">
          <button type="button" className="btn btn--sm" onClick={() => onCopy(claudeConfigSnippet)}>
            Copy snippet
          </button>
        </div>

        {desktop && (
          <>
            <h4 className="settings-gap-top">Local HTTP MCP endpoint</h4>
            <p className="mcp-hint">
              For MCP clients that prefer HTTP. Only available while this app is open. Bound to{' '}
              <code>127.0.0.1</code> — never exposed to the LAN.
            </p>
            <div className="mcp-row">
              <code>{localUrl || 'http://localhost:24115/mcp'}</code>
              <button type="button" className="btn btn--sm" onClick={() => onCopy(localUrl)}>
                Copy URL
              </button>
            </div>
            <div className="mcp-row">
              <span>
                Token: <code>{revealed ?? keychainMasked ?? '(not minted)'}</code>
              </span>
              {keychainHasToken && revealed === null && (
                <button type="button" className="btn btn--sm" onClick={() => void onReveal()}>
                  Reveal (auto-hides 30s)
                </button>
              )}
              {revealed !== null && (
                <button type="button" className="btn btn--sm" onClick={() => onCopy(revealed)}>
                  Copy
                </button>
              )}
            </div>
          </>
        )}
      </details>
    </>
  );
}
